/**
 * Tasas de productividad perdida por NO usar IA — basadas en estudios reales.
 *
 * Fuentes:
 * - GitHub Copilot Study (2023): devs completan tareas 55% más rápido con IA
 *   https://arxiv.org/abs/2302.06590
 * - MIT/Harvard/Microsoft (2024): 26% más tareas completadas con IA (4,867 devs)
 *   https://github.blog/news-insights/research/research-quantifying-github-copilots-impact-on-developer-productivity-and-happiness/
 * - DX Insight (2025): usuarios diarios de IA mergean ~60% más PRs
 *   https://www.secondtalent.com/resources/ai-coding-assistant-statistics/
 * - Salary.com / Glassdoor (2025): Senior dev USA ~$75/hr promedio
 *   https://www.salary.com/research/salary/alternate/senior-software-engineer-hourly-wages
 */

// ── Supuestos base ──────────────────────────────────────────────
// Costo promedio dev senior: $75/hr (USA, Salary.com 2025)
const DEV_COST_PER_HOUR = 75;

// Ganancia de productividad con IA: ~40% (promedio conservador entre
// el 26% de MIT/Harvard y el 55% de GitHub Copilot Study)
const AI_PRODUCTIVITY_GAIN = 0.4;

// Un dev promedio completa ~4 tareas/día (8h) y mergea ~5 PRs/día
const TASKS_PER_DAY = 4;
const PRS_PER_DAY = 5;

// ── Tasas derivadas (lo que se pierde por hora sin IA) ──────────
export const RATES = {
  // Delta de costo: lo que la empresa pierde en eficiencia por hora
  // $75/hr * 40% = $30/hr de productividad no capturada
  costPerHour: DEV_COST_PER_HOUR * AI_PRODUCTIVITY_GAIN,

  // Tareas extra que se podrían completar con IA por hora
  // 4 tareas/8h = 0.5/hr, +40% = 0.2 tareas/hr extra
  tasksPerHour: (TASKS_PER_DAY / 8) * AI_PRODUCTIVITY_GAIN,

  // PRs extra por hora con IA (60% más PRs según DX Insight)
  // 5 PRs/8h = 0.625/hr, * 60% = 0.375/hr extra
  prsPerHour: (PRS_PER_DAY / 8) * 0.6,

  // Minutos ahorrados por hora con IA
  // 60 min * 40% = 24 min/hr ahorrados
  minutesSavedPerHour: 60 * AI_PRODUCTIVITY_GAIN,
};

// ── Tasas por segundo (para el timer) ───────────────────────────
export const RATES_PER_SECOND = {
  cost: RATES.costPerHour / 3600,
  tasks: RATES.tasksPerHour / 3600,
  prs: RATES.prsPerHour / 3600,
  minutesSaved: RATES.minutesSavedPerHour / 3600,
};

// ── Fuentes (para mostrar en la UI) ────────────────────────────
export interface SourceInfo {
  label: string;
  url: string;
  detail: string;
}

export const SOURCES: SourceInfo[] = [
  {
    label: 'GitHub Copilot Study',
    url: 'https://arxiv.org/abs/2302.06590',
    detail: 'Devs completan tareas 55% más rápido con IA (p=0.0017)',
  },
  {
    label: 'MIT / Harvard / Microsoft',
    url: 'https://github.blog/news-insights/research/research-quantifying-github-copilots-impact-on-developer-productivity-and-happiness/',
    detail: '26% más tareas completadas — 4,867 desarrolladores en 3 empresas',
  },
  {
    label: 'DX Insight (51K devs)',
    url: 'https://www.secondtalent.com/resources/ai-coding-assistant-statistics/',
    detail: 'Usuarios diarios de IA mergean ~60% más PRs',
  },
  {
    label: 'Salary.com',
    url: 'https://www.salary.com/research/salary/alternate/senior-software-engineer-hourly-wages',
    detail: 'Dev senior USA: ~$75/hr promedio (2025)',
  },
];

// ── Escenarios (equivalencias de tiempo) ────────────────────────
export interface ScenarioData {
  label: string;
  emoji: string;
  minutes: number;
  cost: number;
  tasks: number;
  prs: number;
  minutesSaved: number;
}

function buildScenario(label: string, emoji: string, minutes: number): ScenarioData {
  const hours = minutes / 60;
  return {
    label,
    emoji,
    minutes,
    cost: Math.round(RATES.costPerHour * hours),
    tasks: Math.round(RATES.tasksPerHour * hours * 10) / 10,
    prs: Math.round(RATES.prsPerHour * hours * 10) / 10,
    minutesSaved: Math.round(RATES.minutesSavedPerHour * hours),
  };
}

export const SCENARIOS: ScenarioData[] = [
  buildScenario('Café', '☕', 15),
  buildScenario('Emails', '📧', 30),
  buildScenario('Reunión', '🤝', 60),
  buildScenario('Almuerzo', '🍽️', 60),
  buildScenario('Día completo', '📅', 480),
];
